// Arabic Unicode ranges:
// - Basic Arabic: \u0600-\u06FF
// - Arabic Supplement: \u0750-\u077F
// - Arabic Extended-A: \u08A0-\u08FF
// - Arabic Extended-B: \u0870-\u089F
// - Arabic Presentation Forms-A: \uFB50-\uFDFF
// - Arabic Presentation Forms-B: \uFE70-\uFEFF

const ARABIC_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0870-\u089F\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Checks if the text contains any Arabic characters
 */
export function containsArabic(text: string): boolean {
  return ARABIC_REGEX.test(text);
}

/**
 * Checks if the text is mainly Arabic (more than 50% of alphabetic characters are Arabic)
 * Used to determine if the overall layout direction should be RTL
 */
export function isMainlyArabic(text: string): boolean {
  // Count Arabic characters
  const arabicMatches = text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0870-\u089F\uFB50-\uFDFF\uFE70-\uFEFF]/g);
  const arabicCount = arabicMatches ? arabicMatches.length : 0;

  // Count Latin characters
  const latinMatches = text.match(/[a-zA-Z]/g);
  const latinCount = latinMatches ? latinMatches.length : 0;

  const totalAlphaChars = arabicCount + latinCount;

  if (totalAlphaChars === 0) {
    return false;
  }

  return arabicCount / totalAlphaChars > 0.5;
}

/**
 * Returns the appropriate font family for the given text
 */
export function getArabicAwareFontFamily(
  text: string,
  defaultFontFamily: string,
  arabicFontFamily: string = "Scheherazade New"
): string {
  return containsArabic(text) ? arabicFontFamily : defaultFontFamily;
}

/**
 * Returns text direction based on content
 */
export function getTextDirection(text: string): "ltr" | "rtl" {
  return isMainlyArabic(text) ? "rtl" : "ltr";
}

/**
 * Returns text alignment based on content
 */
export function getTextAlign(text: string): "left" | "right" {
  return isMainlyArabic(text) ? "right" : "left";
}

/**
 * Represents a segment of text with its script type
 */
export interface TextSegment {
  text: string;
  isArabic: boolean;
}

/**
 * Splits text into segments based on script (Arabic vs non-Arabic).
 * Used for per-segment font application in mixed-script text.
 */
export function splitTextByScript(text: string): TextSegment[] {
  if (text.length === 0) {
    return [];
  }

  const segments: TextSegment[] = [];
  let currentSegment = "";
  let currentIsArabic: boolean | null = null;

  for (const char of text) {
    const charIsArabic = ARABIC_REGEX.test(char);

    // For non-letter characters (spaces, punctuation, numbers), inherit the current segment's script
    // This prevents splitting on every space or punctuation mark
    const isNeutral = !/[\p{L}]/u.test(char);

    if (isNeutral) {
      // Neutral characters stay with current segment
      currentSegment += char;
    } else if (currentIsArabic === null) {
      // First letter character
      currentIsArabic = charIsArabic;
      currentSegment = char;
    } else if (charIsArabic === currentIsArabic) {
      // Same script, continue segment
      currentSegment += char;
    } else {
      // Script changed, save current segment and start new one
      if (currentSegment.length > 0) {
        segments.push({ text: currentSegment, isArabic: currentIsArabic });
      }
      currentSegment = char;
      currentIsArabic = charIsArabic;
    }
  }

  // Push final segment
  if (currentSegment.length > 0) {
    segments.push({ text: currentSegment, isArabic: currentIsArabic ?? false });
  }

  return segments;
}
